// Healthcare Compliance Types - XinAI Features for AlphaCoreWeb
// Following AlphaCoreWeb patterns and conventions

import type { BaseComponentProps } from "./index";

// ================================
// Status Constants
// ================================

export const CompliancePipelineStage = {
  NONE: "NONE",
  RULES_TAGGING: "RULES_TAGGING",
  COMPLIANCE_EVALUATION: "COMPLIANCE_EVALUATION",
} as const;

export type CompliancePipelineStage =
  (typeof CompliancePipelineStage)[keyof typeof CompliancePipelineStage];

export const PIPELINE_STAGE_LABELS: Record<CompliancePipelineStage, string> = {
  NONE: "Not started",
  RULES_TAGGING: "Tagged",
  COMPLIANCE_EVALUATION: "Evaluated",
};

export const ProcessingStatus = {
  PENDING: "PENDING",
  QUEUED: "QUEUED",
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const;

export type ProcessingStatus =
  (typeof ProcessingStatus)[keyof typeof ProcessingStatus];

export const JobType = {
  DOCUMENT_PROCESSING: "DOCUMENT_PROCESSING",
  RULES_TAGGING: "RULES_TAGGING",
  CASE_REEVALUATION: "CASE_REEVALUATION",
  CHECKLIST_DOCUMENT_ANALYZE: "CHECKLIST_DOCUMENT_ANALYZE",
  QUESTIONNAIRE_SUBCATEGORY: "QUESTIONNAIRE_SUBCATEGORY",
  TENANT_PROFILE_GENERATION: "TENANT_PROFILE_GENERATION",
  DOCUMENT_EVALUATION: "DOCUMENT_EVALUATION",
  DOCUMENT_RULE_EVALUATION_SYNTHESIS: "DOCUMENT_RULE_EVALUATION_SYNTHESIS",
  RAG_DOCUMENT_INGEST: "RAG_DOCUMENT_INGEST",
  RAG_RULE_EVALUATION: "RAG_RULE_EVALUATION",
  OSV_EVIDENCE_SLOT_TAG: "OSV_EVIDENCE_SLOT_TAG",
} as const;

export type JobType = (typeof JobType)[keyof typeof JobType];

export const RETRYABLE_JOB_TYPES: JobType[] = [
  JobType.DOCUMENT_PROCESSING,
  JobType.RULES_TAGGING,
  JobType.CASE_REEVALUATION,
  JobType.CHECKLIST_DOCUMENT_ANALYZE,
  JobType.QUESTIONNAIRE_SUBCATEGORY,
  JobType.TENANT_PROFILE_GENERATION,
  JobType.DOCUMENT_RULE_EVALUATION_SYNTHESIS,
  JobType.RAG_DOCUMENT_INGEST,
  JobType.RAG_RULE_EVALUATION,
  JobType.OSV_EVIDENCE_SLOT_TAG,
];

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  DOCUMENT_PROCESSING: "Document Processing",
  RULES_TAGGING: "Rules Tagging",
  CASE_REEVALUATION: "Case Re-evaluation",
  CHECKLIST_DOCUMENT_ANALYZE: "Checklist Document Analyze",
  QUESTIONNAIRE_SUBCATEGORY: "Assign Tenant Subcategories",
  TENANT_PROFILE_GENERATION: "Generate Tenant Profile & Rule Relevance",
  DOCUMENT_EVALUATION: "Document Evaluation",
  DOCUMENT_RULE_EVALUATION_SYNTHESIS: "Rule Synthesis",
  RAG_DOCUMENT_INGEST: "RAG Document Ingest",
  RAG_RULE_EVALUATION: "RAG Rule Evaluation",
  OSV_EVIDENCE_SLOT_TAG: "OSV Evidence Slot Tag",
};

export const FileUploadStatus = {
  PENDING: "pending",
  UPLOADING: "uploading",
  COMPLETED: "completed",
  ERROR: "error",
} as const;

export type FileUploadStatus =
  (typeof FileUploadStatus)[keyof typeof FileUploadStatus];

export const DataType = {
  STRING: "string",
  INTEGER: "integer",
  DECIMAL: "decimal",
  DATE: "date",
  DATETIME: "datetime",
  BOOLEAN: "boolean",
} as const;

export type DataType = (typeof DataType)[keyof typeof DataType];

// ================================
// Core Healthcare Entities
// ================================

export interface UploadedFile {
  id: number;
  filename: string;
  originalName: string;
  fileSize: number;
  uploadTimestamp: string;
  mimeType: string;
  userId: string;
  filePath: string;
  status?: string;
  compliancePipelineStage?: CompliancePipelineStage | null;
}

export interface JobDisplayFieldDto {
  key: string;
  label: string;
  value: string;
}

export interface JobStage {
  id: number;
  stageId?: number; // Alias for id
  jobId: number;
  stageName: string;
  stageStatus: string; // 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'SKIPPED'
  stageData?: Record<string, any>;
  data?: Record<string, any>; // Alias for stageData, contains step_class, duration_seconds, etc.
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessingJob {
  id: number;
  uploadedFileId?: number; // Optional - API may not return this
  status?: ProcessingStatus; // Optional - API may not return this
  jobStatus?: string; // New field: COMPLETED, FAILED, PROCESSING, QUEUED
  documentType?: string;
  documentName?: string; // New field: name of the document being processed
  confidenceScore?: number;
  errorMessage?: string;
  createdAt?: string; // Optional - API may not return this
  completedAt?: string;
  durationSeconds?: number; // New field: job duration in seconds
  uploadedFile?: UploadedFile;
  stages?: JobStage[]; // Optional stages array for detailed job tracking
  // Additional fields from backend
  jobId?: number; // Alias for id
  jobType?: string; // e.g., 'DOCUMENT_PROCESSING'
  documentId?: number; // Alias for uploadedFileId
  workflowId?: number; // Workflow ID
  tenantId?: number;
  assignedToUserId?: number;
  assignedToName?: string;
  workflowName?: string; // Workflow name from workflows table
  startedAt?: string;
  retryCount?: number;
  maxRetries?: number;
  displayFields?: JobDisplayFieldDto[];
  displaySummary?: string | null;
  fileSizeBytes?: number | null;
}

/**
 * Response type for GET /api/job-management/jobs/{id}/stages
 */
export interface JobStagesResponse {
  jobId: number;
  stages: JobStage[];
}

/** Per-document entry from GET /api/job-management/jobs/{id}/document-processing */
export interface JobDocumentProcessing {
  documentId: number;
  fileName: string | null;
  fileSizeBytes: number | null;
  status: "completed" | "in_progress" | "pending";
  /** True while a PROCESSING anchor row has ended_at null — drives a per-file spinner */
  processingActive: boolean;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  /** Null when caller lacks ApiCallCost.Read (240) */
  totalCostUsd: number | null;
  totalLlmDurationMs: number | null;
  llmCallCount: number;
  ocrCallCount: number;
  lastStepName: string | null;
}

export interface JobDocumentProcessingSummary {
  totalDocuments: number;
  completedDocuments: number;
  inProgressDocuments: number;
  pendingDocuments: number;
  /** Null when caller lacks ApiCallCost.Read (240) */
  totalCostUsd: number | null;
}

/** Per-rule entry from GET /api/job-management/jobs/{id}/document-processing when progressMode === "RULE" */
export interface JobRuleProcessing {
  ruleId: number;
  ruleNumber: string;
  ruleTitle: string;
  status: "completed" | "pending" | "not_applicable";
}

export interface JobDocumentProcessingResponse {
  jobId: number;
  tenantId: number;
  /** "RULE" for DOCUMENT_EVALUATION (Phase A) jobs; "DOCUMENT" for all others */
  progressMode?: "DOCUMENT" | "RULE";
  documents: JobDocumentProcessing[];
  /** Populated when progressMode === "RULE"; absent from legacy DOCUMENT-mode responses */
  rules?: JobRuleProcessing[];
  summary: JobDocumentProcessingSummary;
}

/** Single call entry from GET /api/job-management/jobs/{id}/documents/{docId}/api-calls */
export interface JobApiCall {
  id: number;
  callType: "LLM" | "OCR";
  stepName: string;
  llmDurationMs: number | null;
  startedAt: string | null;
  endedAt: string | null;
  calledAt: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  pageCount: number | null;
  /** Null when caller lacks ApiCallCost.Read (240) */
  costUsd: number | null;
  providerId: number | null;
  modelId: number | null;
  providerName: string | null;
  modelName: string | null;
  /** Only present with ApiCallCost.Read */
  prompt: string | null;
  requestPayload: string | null;
  responsePayload: string | null;
}

export interface JobApiCallsResponse {
  jobId: number;
  documentId: number;
  totalElements: number;
  calls: JobApiCall[];
}

export interface ExtractedData {
  id: number;
  processingJobId: number;
  fieldName: string;
  fieldValue: string;
  dataType: DataType;
  createdAt: string;
  confidence?: number;
}

export interface ComplianceRule {
  id: number;
  ruleName: string;
  ruleDefinition?: string;
  ruleDescription?: string;
  manualReference?: string;
  protocolReferences?: string;
  requirements?: string;
  demonstratingCompliance?: string;
  verificationQuestions?: string;
  commonGapsOrRisks?: string;
  relatedConsiderations?: string;
  crossReferences?: string;
  severity?: string;
  priority?: string;
  example?: string;
  createdAt: string;
}

/**
 * Linked Rule DTO
 * Returned by GET /api/document-management/documents/{documentId}/linked-rules
 */
export interface LinkedRuleDto {
  id: number;
  parentId: number;
  ruleCategoryId: number | null;
  ruleSetId: number | null;
  ruleNumber: string;
  title: string;
  description: string;
  ruleText: string;
  requirements: string | null;
  demonstratingCompliance: string | null;
  commonGapsOrRisks: string | null;
  relatedConsiderations: string | null;
  manualReference: string | null;
  pageNumber: number | null;
  status: number;
}

/** Single item from batch evaluation response */
export interface BatchEvaluationItem {
  documentId: number;
  ruleIds: number[];
  jobId: number;
}

/** Response from POST /api/document-management/documents/batch-evaluation */
export interface BatchEvaluationResponse {
  rulesCovered: number;
  messagesSent: number;
  items: BatchEvaluationItem[];
}

/** Single item from POST /api/document-management/documents/document-rule-synthesis/trigger */
export interface SynthesisItem {
  ruleId: number;
  ruleNumber: string;
  jobId: number;
}

/** Response from POST /api/document-management/documents/document-rule-synthesis/trigger */
export interface DocumentRuleSynthesisTriggerResponse {
  warning: boolean;
  warningMessage: string | null;
  pendingPhaseAJobs: number;
  totalRulesConsidered: number;
  rulesWithDocuments: number;
  rulesWithoutDocuments: number;
  synthesisJobsCreated: number;
  casesCreated: number;
  rulesSkippedExistingCase: number;
  rulesSkippedTerminalCase: number;
  synthesisItems: SynthesisItem[];
  createdCases: CreatedCaseItem[];
}

/** Response from POST /api/document-management/documents/compliance-run/trigger */
export interface ComplianceRunTriggerResponse {
  totalRulesConsidered: number;
  rulesWithDocuments: number;
  rulesWithoutDocuments: number;
  messagesSent: number;
  casesCreated: number;
  rulesSkippedExistingCase: number;
  rulesSkippedTerminalCase: number;
  evaluationItems: EvaluationItem[];
  createdCases: CreatedCaseItem[];
}

export interface EvaluationItem {
  ruleId: number;
  eventType: string;
  documentIds: number[] | null;
  referenceId: number;
}

export interface CreatedCaseItem {
  caseId: number;
  ruleId: number;
  ruleTitle: string;
}
/** Single item from batch rules tagging response */
export interface BatchRulesTaggingItem {
  documentId: number;
  jobId: number;
}

/** Response from POST /api/document-management/documents/batch-rules-tagging */
export interface BatchRulesTaggingResponse {
  documentsConsidered: number;
  skippedNoS3: number;
  documentRulesRowsUpdated: number;
  messagesSent: number;
  items: BatchRulesTaggingItem[];
}

/** Response from POST /api/document-management/documents/{id}/rules-tagging/retrigger */
export type DocumentRulesRetaggingTriggerResponse = BatchRulesTaggingResponse;

/**
 * Document Type DTO
 * Matches the API response structure from /api/document-management/document-types
 */
export interface DocumentType {
  id: number;
  name: string;
  description: string;
  tenantId: number | null; // null for global document types
  isGlobal: boolean; // true for global types, false for tenant-specific
  createdAt: string; // ISO 8601 format
  updatedAt: string; // ISO 8601 format
  createdBy: string | null; // User name who created (extracted from JWT)
  updatedBy: string | null; // User name who last updated (extracted from JWT)
  isActive?: boolean; // Optional for backward compatibility
}

export interface ExtractionPrompt {
  id: number;
  documentTypeId: number | null; // Can be null for global/system prompts
  documentType?: DocumentType; // Optional for UI display
  jobType: string;
  prompt: string; // API uses "prompt" not "promptText"
  version: string; // API uses string version
  effectiveFrom: string;
  effectiveTill: string;
  tenantId?: number | null; // Optional - global prompts have null tenantId
  isGlobal?: boolean; // Global prompts cannot be edited by tenants
  isActive?: boolean; // Keep for backward compatibility
  createdAt?: string;
  updatedAt?: string;
}

// ================================
// UI State Types (Following AlphaCoreWeb patterns)
// ================================

export interface FileUploadProgress {
  file: File;
  progress: number;
  status: FileUploadStatus;
  error?: string;
  uploadedFileId?: number;
  processingJob?: ProcessingJob;
  extractionProgress?: number;
}

export interface ProcessingStats {
  pendingCount: number;
  queuedCount: number;
  runningCount: number;
  completedCount: number;
  failedCount: number;
  cancelledCount: number;
  averageProcessingDurationSeconds: number | null;
}

// ================================
// API Response Types (Following AlphaCoreWeb ApiResponse pattern)
// ================================

export interface HealthcareApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

// ================================
// Filter and Query Types
// ================================

export interface ProcessingJobFilters {
  status?: ProcessingStatus[];
  documentType?: string;
  dateFrom?: string;
  dateTo?: string;
  createdAt?: string;
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface FileFilters {
  mimeType?: string[];
  dateFrom?: string;
  dateTo?: string;
  searchText?: string; // API parameter name for document search
  pageNumber?: number;
  pageSize?: number;
  compliancePipelineStage?: number;
  compliancePipelineStages?: CompliancePipelineStage[];
}

export interface ComplianceRuleFilters {
  severity?: string[];
  priority?: string[];
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
}

// ================================
// Form Data Types
// ================================

export interface FileUploadFormData {
  files: File[];
  description?: string;
  tags?: string[];
}

/**
 * Document Upload - Presign Flow Types
 * Based on /api/document-management endpoints
 */
export interface DocumentUploadRequest {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  documentType?: string | null;
}

export interface DocumentUploadResponse {
  documentId: number;
  uploadUrl: string;
  s3Bucket: string;
  s3Key: string;
  filename: string;
  contentType: string;
  expiresIn: number;
}

export interface DocumentUploadCompleteRequest {
  documentId: number;
  sizeBytes?: number;
  /**
   * When true, only stores the file and triggers rules tagging.
   * When false (default), triggers full compliance processing + rules extraction.
   */
  uploadOnly?: boolean;
}

export interface CompleteResponse {
  success: boolean;
  documentId: number;
  jobId: number;
  message: string;
  s3Key: string;
  s3Bucket: string;
  duplicate: boolean;
  existingDocumentId?: number;
}

export interface DocumentTypeFormData {
  name: string;
  description: string;
}

/**
 * Request DTOs for Document Types API
 * These are type aliases for clarity and match the API documentation
 */
export type CreateDocumentTypeRequestDto = DocumentTypeFormData;
export type UpdateDocumentTypeRequestDto = Partial<DocumentTypeFormData>;

export interface ExtractionPromptFormData {
  documentTypeId: number | null;
  jobType: string;
  prompt: string; // API uses "prompt" not "promptText"
  version: string;
  effectiveFrom: string;
  effectiveTill: string;
  /** null = Global, number = specific tenant (admin only) */
  tenantId?: number | null;
}

export interface ExtractionPromptUpdateData {
  jobType: string;
  prompt: string;
  version: string;
  effectiveFrom: string;
  effectiveTill: string;
  /** null = Global, number = specific tenant (admin only) */
  tenantId?: number | null;
}

export interface GlobalPromptFormData {
  documentTypeId: number;
  jobType: string;
  prompt: string;
  version: string;
  effectiveFrom: string;
  effectiveTill: string;
}

export interface ExtractedDataUpdateRequest {
  fieldValue: string;
}

// ================================
// Component Props (Following AlphaCoreWeb BaseComponentProps)
// ================================

export interface HealthcarePageProps extends BaseComponentProps {
  title?: string;
}

export interface FileUploadComponentProps extends BaseComponentProps {
  onFilesUploaded?: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: Record<string, string[]>;
}

export interface ProcessingJobComponentProps extends BaseComponentProps {
  job: ProcessingJob;
  onStatusUpdate?: (job: ProcessingJob) => void;
}

export interface ComplianceRuleComponentProps extends BaseComponentProps {
  rule: ComplianceRule;
  expanded?: boolean;
  onToggleExpand?: (ruleId: number) => void;
}

// ================================
// Utility Types
// ================================

export interface ExportOptions {
  format: "csv" | "json";
  includeMetadata?: boolean;
}

export interface DownloadRequest {
  fileId: number;
  fileName: string;
}

export interface BulkActionRequest {
  fileIds: number[];
  action: "delete" | "reprocess" | "download";
}

// ================================
// Dashboard Metrics Types
// ================================

export interface HealthcareDashboardMetrics {
  totalFiles: number;
  totalProcessingJobs: number;
  completedJobs: number;
  failedJobs: number;
  pendingJobs: number;
  processingJobs: number;
  totalComplianceRules: number;
  averageProcessingTime: number;
  successRate: number;

  // Trends
  uploadTrend: Array<{
    date: string;
    count: number;
  }>;

  processingTrend: Array<{
    date: string;
    completed: number;
    failed: number;
  }>;

  // File type distribution
  fileTypeDistribution: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
}

// ================================
// Error Types (Following AlphaCoreWeb patterns)
// ================================

export interface HealthcareApiError {
  message: string;
  code?: string;
  statusCode: number;
  details?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

// ================================
// Real-time Event Types
// ================================

export interface ProcessingStatusEvent {
  jobId: number;
  status: ProcessingStatus;
  confidenceScore?: number;
  errorMessage?: string;
  timestamp: string;
}

export interface FileUploadEvent {
  fileId: number;
  status: FileUploadStatus;
  progress?: number;
  error?: string;
  timestamp: string;
}

// ================================
// Configuration Types
// ================================

export interface HealthcareSystemConfig {
  maxFileSize: number;
  allowedFileTypes: string[];
  maxBatchSize: number;
  pollingInterval: number;
  retryAttempts: number;
  confidenceThreshold: number;
}

export interface FileTypeConfig {
  extension: string;
  mimeType: string;
  maxSize?: number;
  processingSupported: boolean;
}

// ================================
// Search and Analytics Types
// ================================

export interface SearchResults<T> {
  items: T[];
  totalCount: number;
  query: string;
  filters: Record<string, any>;
  executionTime: number;
}

export interface AnalyticsData {
  period: string;
  metrics: {
    filesUploaded: number;
    jobsCompleted: number;
    averageProcessingTime: number;
    errorRate: number;
  };
  trends: {
    daily: Array<{ date: string; value: number }>;
    weekly: Array<{ week: string; value: number }>;
    monthly: Array<{ month: string; value: number }>;
  };
}

// ================================
// Pagination Types (matching backend API response)
// ================================

export interface PaginatedResponse<T> {
  content: T[];
  number: number;
  size: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// ================================
// Bulk Retry Types
// ================================

export interface BulkRetryJobFailureDto {
  jobId: number;
  message: string;
}

export interface BulkRetryFailedJobsResponse {
  matchedCount: number;
  attemptedCount: number;
  successCount: number;
  failureCount: number;
  failures: BulkRetryJobFailureDto[];
}

// ================================
// Export all types for convenience
// ================================

export * from "./index"; // Re-export base types
